import { ArrowDownToLine } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryBreakdownRadarSection from '../shared/CategoryBreakdownRadarSection';
import CognitiveFitness from '../shared/CognitiveFitness';
import PerformanceInfo from '../shared/PerformanceInfo';
import ProfileInformation from '../shared/ProfileInformation';
import ReportHeader from '../shared/ReportHeader';
import {
    calculateBMI,
    mapPerformanceResults,
    mapProfile,
    mapReportHeaderData,
    mapTraineeReportData,
} from '../shared/utils';
import {
    useAssessmentResultsQuery,
    useFetchTraineePerformanceResultsQuery,
    useFetchUserDocumentQuery,
    useIncompleteModulesQuery,
} from './TraineeReports.api';
import TrainersNotesEditor from './TrainersNotesEditor';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import Loader from '@/components/common/Loader';
import MarkdownRenderer from '@/components/common/MarkdownRenderer';
import MetricCard from '@/components/common/MetricCard';
import PageHeader from '@/components/common/PageHeader';
import Section from '@/components/common/Section';
import { generateAndDownloadPdf } from '@/utils/pdf/exportPdf';
import useAgeCalculation from '@/utils/useAgeCalculation';
import useDateFormats from '@/utils/useDateFormats';

interface OverallTraineeReportPageProps {
    handleBack: () => void;
    assessmentRunId: string;
    userId: string;
    programId?: string;
    programName?: string;
    startDate?: Date;
    endDate?: Date;
    isDetail: boolean;
    editMode?: boolean;
}

const OverallTraineeReportPage = ({
    handleBack,
    assessmentRunId,
    userId,
    programId,
    programName,
    startDate,
    endDate,
    isDetail,
    editMode,
}: OverallTraineeReportPageProps) => {
    const { t } = useTranslation('overallTraineeReport');
    const exportRef = useRef<HTMLDivElement>(null);
    const [downloading, setDownloading] = useState(false);
    const { formatDate } = useDateFormats();
    const { formatAge } = useAgeCalculation();
    const queryFilter = useMemo(
        () => ({
            assessmentRunIds: assessmentRunId ? [assessmentRunId] : [],
            userIds: [userId],
        }),
        [assessmentRunId, userId]
    );
    const hasAutoExported = useRef(false);

    // Get completed assessment results
    const {
        loading: loadingCompleted,
        data: completedData,
        error: completedError,
    } = useAssessmentResultsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                ...queryFilter,
            },
        },
        skip: !assessmentRunId || !userId,
    });

    const {
        loading: loadingPerformance,
        data: performanceData,
        error: performanceError,
    } = useFetchTraineePerformanceResultsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                ...queryFilter,
            },
        },
        skip: !assessmentRunId || !userId,
    });

    // Get incomplete modules
    const {
        loading: loadingIncomplete,
        data: incompleteData,
        error: incompleteError,
    } = useIncompleteModulesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            programId: programId || '',
            userId,
            assessmentRunId: assessmentRunId || '',
        },
        skip: !programId || !userId || !assessmentRunId,
    });

    // Get user document
    const {
        loading: loadingUser,
        data: userData,
        error: userError,
    } = useFetchUserDocumentQuery({
        fetchPolicy: 'cache-and-network',
        variables: { userId },
        skip: !userId,
    });

    const loading = loadingCompleted || loadingIncomplete || loadingPerformance || loadingUser;
    const error = completedError || incompleteError || performanceError || userError;
    const assessmentResults = useMemo(() => completedData?.assessmentResults.items ?? [], [completedData]);
    const teamResilienceItems = completedData?.assessmentResults.teamResilienceItems ?? [];
    const incompleteItems = incompleteData?.incompleteModules ?? [];
    const hasAnyAssessmentItems = assessmentResults.length > 0 || incompleteItems.length > 0;

    const exportPdf = async (
        ref: React.RefObject<HTMLDivElement | null>,
        setDownloading: (downloading: boolean) => void
    ) => {
        if (ref.current) {
            setDownloading(true);
            try {
                await generateAndDownloadPdf(ref.current, 'IndividualReport.pdf', { padding: '12mm' }, 'Export');
            } finally {
                setDownloading(false);
            }
        }
    };

    const handleDownload = useCallback(() => exportPdf(exportRef, setDownloading), [exportRef]);

    const profile = mapProfile(t, userData?.user?.__typename === 'TenantUser' ? userData.user : null, formatAge);

    const reportHeaderData = mapReportHeaderData(
        t,
        formatDate,
        programName ?? '',
        startDate ?? new Date(),
        endDate ?? new Date(),
        assessmentResults,
        incompleteItems
    );

    const traineeReportData = mapTraineeReportData(t, assessmentResults, teamResilienceItems, incompleteItems);
    const { trainerNotes, physiologicalFitness, psychologicalFitness, cognitiveFitness } = traineeReportData;

    const performance = mapPerformanceResults(t, formatDate, performanceData?.performanceResults?.items ?? []);

    const weight = useMemo(() => {
        const inbodyResult = assessmentResults.find(r => r.result.__typename === 'InbodyCompositionResult')?.result;

        if (!inbodyResult || inbodyResult.__typename !== 'InbodyCompositionResult' || !inbodyResult.weightKg) {
            return null;
        }
        return inbodyResult.weightKg;
    }, [assessmentResults]);

    profile.weightKg = weight?.toFixed(2) ?? t('traineeReports:na');

    // Ensure height is a valid positive number before attempting BMI calculation
    const heightCm = Number(profile.heightCm);
    const hasValidHeight = Number.isFinite(heightCm) && heightCm > 0;
    profile.bmi = weight && hasValidHeight ? calculateBMI(weight, heightCm / 100).toFixed(2) : t('traineeReports:na');

    const headerActionButton = {
        label: downloading ? t('export.loading') : t('export.button'),
        onClick: handleDownload,
        icon: <ArrowDownToLine className="size-5" />,
        disabled: downloading,
    };

    const reportContent = useMemo(() => {
        return (
            <div className="w-full space-y-6 overflow-auto">
                <ReportHeader reportHeaderData={reportHeaderData} name={profile.name} />

                <div className="grid gap-6 sm:grid-cols-12">
                    <ProfileInformation profile={profile} className="sm:col-span-4" />
                    <PerformanceInfo performance={performance} className="sm:col-span-8" />
                </div>

                {/* Physiological Fitness */}
                {physiologicalFitness.length > 0 && (
                    <Section
                        title={t('sections.physiological.title')}
                        subtitle={t('sections.physiological.subtitle')}
                        accent="muted"
                        className="pdf-avoid-break"
                    >
                        <div className="space-y-5">
                            {physiologicalFitness.map(m => (
                                <Section
                                    title={m.name}
                                    subtitle={m.description}
                                    key={m.moduleDefinitionIdentifier}
                                    iconKey={m.moduleDefinitionIdentifier}
                                    accent="purple"
                                >
                                    <div className="mt-2 grid gap-3 sm:grid-cols-4">
                                        {m.result.map(r => (
                                            <MetricCard key={r.label} label={r.label} value={r.value} />
                                        ))}
                                    </div>
                                </Section>
                            ))}
                        </div>
                    </Section>
                )}

                {/* Psychological Fitness */}
                {psychologicalFitness.length > 0 && (
                    <Section
                        title={t('sections.psychological.title')}
                        subtitle={t('sections.psychological.subtitle')}
                        accent="muted"
                        className="pdf-avoid-break"
                    >
                        <div className="space-y-5">
                            {psychologicalFitness.map(m => (
                                <CategoryBreakdownRadarSection
                                    key={m.moduleDefinitionIdentifier}
                                    title={m.name}
                                    subtitle={m.description}
                                    iconKey={m.moduleDefinitionIdentifier}
                                    accent="blue"
                                    radarGroups={m.radarGroups ?? []}
                                />
                            ))}
                        </div>
                    </Section>
                )}

                {/* Cognitive Fitness */}
                {cognitiveFitness.length > 0 && (
                    <Section
                        title={t('sections.cognitive.title')}
                        subtitle={t('sections.cognitive.subtitle')}
                        accent="muted"
                        className="pdf-avoid-break"
                    >
                        <div className="space-y-5">
                            {cognitiveFitness.map(test => (
                                <CognitiveFitness
                                    key={test.moduleDefinitionIdentifier}
                                    name={test.name}
                                    description={test.description}
                                    moduleDefinitionIdentifier={test.moduleDefinitionIdentifier}
                                    result={test.result}
                                />
                            ))}
                        </div>
                    </Section>
                )}

                <Section title={t('sections.trainerNotes.title')} accent="muted">
                    <MarkdownRenderer content={trainerNotes} />
                </Section>
            </div>
        );
    }, [
        profile,
        performance,
        trainerNotes,
        physiologicalFitness,
        psychologicalFitness,
        cognitiveFitness,
        reportHeaderData,
        t,
    ]);

    useEffect(() => {
        if (
            loading ||
            error ||
            !hasAnyAssessmentItems ||
            isDetail ||
            hasAutoExported.current ||
            !exportRef.current ||
            !reportContent
        ) {
            return;
        }
        const timeoutId = setTimeout(async () => {
            await exportPdf(exportRef, setDownloading);
            hasAutoExported.current = true;
            handleBack?.();
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [isDetail, loading, error, hasAnyAssessmentItems, exportRef, handleBack, reportContent]);

    if (error) {
        return (
            <EmptyContainer>
                <Empty title={t('empty.title')} />
            </EmptyContainer>
        );
    }

    if (loading && !hasAnyAssessmentItems) {
        return <Loader />;
    }

    return (
        <div className="w-full min-w-[1000px] overflow-auto">
            <PageHeader
                title={t('header.back')}
                actionBack={{ showBackButton: true, onBack: handleBack }}
                actionButton={editMode ? undefined : headerActionButton}
                className="w-full min-w-[600px]"
            />

            {editMode && (
                <div className="mb-6 px-0.5">
                    <TrainersNotesEditor assessmentRunId={assessmentRunId} userId={userId} onBack={handleBack} />
                </div>
            )}
            {reportContent}

            <div className="absolute top-[-10000px] left-[-10000px]">
                <div className="w-[1280px]" ref={exportRef}>
                    {reportContent}
                </div>
            </div>
        </div>
    );
};

export default OverallTraineeReportPage;
